'use strict';

define('vb/action/builtin/callComponentMethodAction',['vb/action/action', 'vb/private/log', 'ojs/ojcomponentcore', 'vb/private/utils'],
  (Action, Log, ojCC, Utils) => {
    /** @typedef {import('vb/action/action').Outcome} Outcome */

    const logger = Log.getLogger('/vb/private/stateManagement/callComponentMethodAction');

    /**
     * Represents an action that calls a method on a specific HTML element.
     *
     * @extends Action
     * @category Action
     */
    class CallElementMethod extends Action {
      /**
       * Creates a new instance of CallElementMethod.
       *
       * @param {string} id - The unique identifier for this action.
       * @param {string} label - The human-readable label for this action.
       */
      constructor(id, label) {
        super(id, label);
        this.context = null;
      }

      /**
       * Performs the specified method on the given element.
       *
       * @param {Object} parameters - The parameters object containing information about the method to call.
       * @param {Element|undefined} parameters.component - The component element. If provided, it will be used directly.
       * @param {string} parameters.method - The name of the method to call on the element.
       * @param {Array|undefined} parameters.params - An array of parameters to pass to the method.
       * @param {string|undefined} parameters.selector - A CSS selector to find the element. If provided, it will
       * override the component property.
       *
       * @returns {Promise<Outcome>} A promise resolving to an Outcome object representing the result of the
       * method call.
       */
      perform(parameters) {
        let element = null;
        if (parameters.selector) {
          const container = this.context.container;
          if (container && container.parentOJModuleNode) {
            const parentOjModule = container.parentOJModuleNode;
            element = parentOjModule.querySelector(parameters.selector);
          }
          if (element === null) {
            logger.warn('Target element with selector', parameters.selector,
              'is not present inside the container', container.id,
              // eslint-disable-next-line max-len
              '. It is recommended that the selector provided with the action be locatable within the current container.',
              'Attempting to search for target element outside the container.');
            element = document.querySelector(parameters.selector);
          }
        } else {
          element = parameters.component;
        }

        const methodName = parameters.method;
        const params = parameters.params || [];

        if (element && methodName) {
          // make sure the parameters are plain objects instead of proxies
          const clonedParams = [];
          if (params) {
            Utils.cloneObject(params, clonedParams);
          }

          let returnValue;
          try {
            // @ts-ignore
            returnValue = ojCC.callComponentMethod(element, methodName, ...clonedParams);
          } catch (e) {
            return Action.createFailureOutcome('Error during CallElementMethod', e);
          }

          // handle both sync and async methods
          return Promise.resolve(returnValue)
            .then((result) => Action.createSuccessOutcome(result))
            .catch((e) => Action.createFailureOutcome('Error during CallElementMethod', e));
        }

        const msg = `Missing parameter, element='${element}', methodName='${methodName}'`;
        logger.error(msg);

        return Action.createFailureOutcome('Error during CallElementMethod', new Error(msg)); // default
      }

      /**
       * Sets the context for this CallElementMethod instance.
       *
       * @param {Object} context - The context object to be stored in this instance.
       */
      setContext(context) {
        this.context = context;
      }
    }

    return CallElementMethod;
  });

